// Small shared pieces of the Team chat (staff chat MVP, SR-14), ported from Antonin's catalog source
// (Signal DS `catalog/src/components/TeamChat/TeamChat.tsx`): the reconnecting strip, the loading lines and the
// composer (one line that grows to four; Enter sends, Shift+Enter is a new line; the send button is blue only when
// there is text).
import { useRef, useState } from 'react';
import { Send, WifiOff } from 'lucide-react';
import { Button, Skeleton, TextArea } from '@scripsteam/scrips-signal-ds';

export const RECONNECTING = 'Reconnecting… What you send waits and goes when the connection is back.';
export const MAX_BODY = 4000;

export function Reconnecting() {
  return (
    <div className="team-chat-strip" role="status" data-slot="team-chat-reconnecting">
      <WifiOff aria-hidden />
      <span>{RECONNECTING}</span>
    </div>
  );
}

/** The catalog's SkeletonLines: three DS Skeleton lines. */
export function SkeletonLines({ label }: { label: string }) {
  return (
    <div className="team-chat-skeleton" role="status" aria-label={label}>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

export interface ComposerProps {
  placeholder?: string;
  /** Called with the trimmed text; the box empties and keeps the focus. */
  onSend: (body: string) => void;
  disabled?: boolean;
}

export function Composer({ placeholder = 'Write a message…', onSend, disabled }: ComposerProps) {
  const [v, setV] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const text = v.trim();
  const send = () => {
    if (!text || text.length > MAX_BODY || disabled) return;
    onSend(text);
    setV('');
    box.current?.focus();
  };
  return (
    <div className="team-chat-composer" data-slot="team-chat-composer">
      <TextArea
        ref={box}
        rows={1}
        aria-label="Message"
        placeholder={placeholder}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send();
          }
        }}
      />
      <Button type="button" variant={text ? 'primary' : 'tertiary'} size="sm" icon={<Send aria-hidden />} aria-label="Send" disabled={!text || text.length > MAX_BODY || disabled} onClick={send} />
    </div>
  );
}
